import { IconType } from '@react-icons/all-files'
import * as React from 'react'

import { IconGoBack } from '~ui/components/icons/specific/IconChevrons'
import { GridRow } from '~ui/components/layout/Box'
import { TSingleRadius } from '~ui/styleGuide/radiuses'

import { Button } from '../Button/Button'
import { ButtonIcon } from '../ButtonIcon/ButtonIcon'

type TProps = RequiredChildren &
  TestingProps & {
    /**
     * color of the main button
     * also used to deduce default color of the text (in children)
     */
    bgColor: PropsOf<typeof Button>['bgColor']
    /**
     * sets buttons padding values
     */
    buttonSize?: PropsOf<typeof Button>['size']
    /**
     * key of radius,
     * applies to both buttons (only the outer sides)
     */
    radius?: TSingleRadius
    /**
     * value with unit (eg. `20rem`),
     * used to limit the width of the whole group
     */
    maxWidth?: PropsOf<typeof GridRow>['maxWidth']
    /**
     * sets main button loading state
     */
    mainButtonState?: PropsOf<typeof Button>['state']
    /**
     * options to disable `backBtn`, `mainBtn` or both (`all`)
     */
    isBackDisabled?: boolean
    /**
     * callback function called on back button click
     */
    onBackClick: PropsOf<typeof ButtonIcon>['onClick']
    /**
     * custom icon on the back button
     */
    backIcon?: IconType
    /**
     * callback function called on main button click
     */
    onClick: PropsOf<typeof Button>['onClick']
    /**
     * onClick used when the button is in `blocked` state
     */
    onBlockedClick?: PropsOf<typeof Button>['onBlockedClick']
  }

/**
 * renders main button (with children inside) and on the left side of it, a small ButtonIcon
 * with < icon (default, can be customized by the `backIcon` prop) (typically used to navigate back)
 */
export const ButtonWithBack: React.FC<TProps> = ({
  mainButtonState,
  bgColor,
  onClick,
  maxWidth,
  children,
  onBackClick,
  onBlockedClick,
  radius,
  backIcon = IconGoBack,
  buttonSize = `medium`,
  isBackDisabled = false,
  ...dataProps
}) => {
  return (
    <GridRow maxWidth={maxWidth} columnGap={`small`} gridTemplateColumns={`auto 1fr`} flexGrow={1}>
      <ButtonIcon
        bgColor={`secondary`}
        size={buttonSize}
        radius={radius}
        icon={backIcon}
        iconSize={`1.2rem`}
        onClick={onBackClick}
        aria-label={`Go back`}
        state={isBackDisabled ? `disabled` : `enabled`}
        padding={{ vertical: `small`, horizontal: `medium` }}
      />
      <Button
        size={buttonSize}
        radius={radius}
        bgColor={bgColor}
        onClick={onClick}
        state={mainButtonState}
        onBlockedClick={onBlockedClick}
        width={`100%`}
        {...dataProps}
      >
        {children}
      </Button>
    </GridRow>
  )
}
